// ============================================================
// Shared data + estimation models for the BigLoads calculators.
// All numbers are either REAL (median day-rates from 1,053 live
// listings, 2026-07-30) or clearly-labeled conservative estimates.
// Nothing here is fabricated as a hard stat; the income model is
// explicitly an estimator with user-adjustable sliders.
// ============================================================

// ---- REAL median day-rates ($/day) by trailer type ----
// Source: bigloads/tmp/rates-by-type.js against the public
// Marketplace API (1,053 priced live listings).
export const DAY_RATES = {
  utility: 75,
  carhauler: 120,
  dump: 150,
  enclosed: 125,
  cargo: 90,
  equipment: 120,
  flatbed: 137.5,
  gooseneck: 165,
  atv: 97,
};

export const TYPE_LABELS = {
  utility: 'Utility Trailer',
  carhauler: 'Car Hauler',
  dump: 'Dump Trailer',
  enclosed: 'Enclosed Trailer',
  cargo: 'Cargo Trailer',
  equipment: 'Equipment Trailer',
  flatbed: 'Flatbed Trailer',
  gooseneck: 'Gooseneck Trailer',
  atv: 'ATV / Powersport Trailer',
};

// ---- Savings / income model ------------------------------------

// ZIP first-3 (SCF) -> population-density tier. Only the clear
// high/low-density prefixes are mapped; everything else = suburban.
// This nudges a DEFAULT; the user always controls the final slider.
export const DENSITY_TIERS = {
  // Major metros (high demand)
  '100': 'metro', '101': 'metro', '102': 'metro', '103': 'metro', '104': 'metro', // NYC
  '112': 'metro', '113': 'metro', '114': 'metro', // Brooklyn/Queens
  '606': 'metro', '607': 'metro', // Chicago
  '900': 'metro', '902': 'metro', '904': 'metro', '905': 'metro', // LA
  '941': 'metro', '940': 'metro', // SF/Bay
  '981': 'metro', // Seattle
  '021': 'metro', '022': 'metro', // Boston
  '200': 'metro', '202': 'metro', '203': 'metro', // DC
  '331': 'metro', '332': 'metro', '333': 'metro', // Miami
  '770': 'metro', '772': 'metro', // Houston
  '752': 'metro', '751': 'metro', // Dallas
  '191': 'metro', // Philadelphia
  '300': 'metro', '303': 'metro', // Atlanta
  '850': 'metro', // Phoenix
  '891': 'metro', // Las Vegas
  // Urban (solid demand)
  '212': 'urban', '480': 'urban', '481': 'urban', '482': 'urban', // Baltimore/Detroit
  '972': 'urban', '971': 'urban', // Portland
  '787': 'urban', '733': 'urban', // Austin
  '802': 'urban', '803': 'urban', // Denver
  '441': 'urban', '442': 'urban', // Cleveland
  '432': 'urban', // Columbus
  // Rural / sparse
  '590': 'rural', '591': 'rural', '592': 'rural', '593': 'rural', // MT
  '820': 'rural', '821': 'rural', '822': 'rural', '824': 'rural', '831': 'rural', // WY
  '834': 'rural', '835': 'rural', // ID
  '577': 'rural', '576': 'rural', // SD
  '585': 'rural', '586': 'rural', // ND
  '677': 'rural', '679': 'rural', // rural KS
  '698': 'rural', // rural NE
};

export const TIER_MULT = { metro: 1.4, urban: 1.15, suburban: 1.0, rural: 0.65 };
export const TIER_LABELS = {
  metro: 'major metro',
  urban: 'urban',
  suburban: 'suburban',
  rural: 'rural',
};

// Conservative base rentals/month per type (part-time owner reality).
export const BASE_RENTALS = {
  utility: 5,
  carhauler: 4,
  dump: 6,
  enclosed: 4.5,
  cargo: 5.5,
  equipment: 4,
  flatbed: 4.5,
  gooseneck: 3.5,
  atv: 4.5,
};

// Sane default rental length (days) per type. No real data; user-adjustable.
export const DEFAULT_LENGTH = {
  utility: 1.5,
  carhauler: 2.5,
  dump: 1.5,
  enclosed: 2.0,
  cargo: 2.0,
  equipment: 3.0,
  flatbed: 2.0,
  gooseneck: 2.5,
  atv: 2.0,
};

export const getDensityTier = zip => {
  if (!zip || zip.length < 3) return 'suburban';
  return DENSITY_TIERS[zip.slice(0, 3)] || 'suburban';
};

export const getDefaultRentalsPerMonth = (zip, type) => {
  const tier = getDensityTier(zip);
  const base = BASE_RENTALS[type] || 4;
  return Math.round(base * (TIER_MULT[tier] || 1) * 10) / 10;
};

// Other platforms commonly take ~15-30%; we use 30% as the headline
// "up to" figure (host-side service fee on peer-to-peer rental platforms).
export const OTHER_PLATFORM_FEE = 0.3;

export const calcIncome = (type, rentalsPerMonth, lengthDays) => {
  const rate = DAY_RATES[type] || 0;
  const grossPerRental = rate * lengthDays;
  const monthlyGross = grossPerRental * rentalsPerMonth;
  const bigloadsNet = monthlyGross; // 0% commission
  const otherNet = monthlyGross * (1 - OTHER_PLATFORM_FEE);
  return {
    rate,
    grossPerRental: Math.round(grossPerRental),
    monthlyGross: Math.round(monthlyGross),
    bigloadsNet: Math.round(bigloadsNet),
    otherNet: Math.round(otherNet),
    monthlyDiff: Math.round(bigloadsNet - otherNet),
    yearlyDiff: Math.round((bigloadsNet - otherNet) * 12),
  };
};

// ---- Towing capacity reference database --------------------------
// Manufacturer-published MAX tow ratings (properly equipped, best-case
// trim). Real, conservative, well-known figures. Actual capacity varies
// by trim/engine/axle/package — the tool tells users to verify their
// door-jamb / owner's manual. These are for ballpark planning only.
export const TOW_VEHICLES = {
  'ford-f150': { label: 'Ford F-150', max: 13500 },
  'ford-f250': { label: 'Ford F-250 Super Duty', max: 22800 },
  'ford-f350': { label: 'Ford F-350 Super Duty', max: 28000 },
  'ford-ranger': { label: 'Ford Ranger', max: 7500 },
  'chevy-silverado-1500': { label: 'Chevy Silverado 1500', max: 13300 },
  'chevy-silverado-2500': { label: 'Chevy Silverado 2500HD', max: 18510 },
  'chevy-colorado': { label: 'Chevy Colorado', max: 7700 },
  'ram-1500': { label: 'RAM 1500', max: 11550 },
  'ram-2500': { label: 'RAM 2500', max: 20000 },
  'ram-3500': { label: 'RAM 3500', max: 37090 },
  'gmc-sierra-1500': { label: 'GMC Sierra 1500', max: 13300 },
  'gmc-sierra-2500': { label: 'GMC Sierra 2500HD', max: 18510 },
  'toyota-tundra': { label: 'Toyota Tundra', max: 12000 },
  'toyota-tacoma': { label: 'Toyota Tacoma', max: 6800 },
  'nissan-titan': { label: 'Nissan Titan', max: 9320 },
  'nissan-frontier': { label: 'Nissan Frontier', max: 6720 },
  'jeep-gladiator': { label: 'Jeep Gladiator', max: 7700 },
  'jeep-grand-cherokee': { label: 'Jeep Grand Cherokee', max: 7200 },
  'chevy-tahoe': { label: 'Chevy Tahoe / Suburban', max: 8400 },
  'ford-expedition': { label: 'Ford Expedition', max: 9300 },
  'toyota-4runner': { label: 'Toyota 4Runner', max: 6000 },
  'toyota-sequoia': { label: 'Toyota Sequoia', max: 9520 },
  'honda-ridgeline': { label: 'Honda Ridgeline', max: 5000 },
  'ford-explorer': { label: 'Ford Explorer', max: 5600 },
  other: { label: 'Other / not listed', max: null },
};

// Typical EMPTY (dry) weights for common rental trailer types, lbs.
// Ballpark ranges for planning; real weight is on the trailer's VIN plate.
export const TRAILER_EMPTY_WEIGHT = {
  utility: 1200,
  carhauler: 2200,
  dump: 3200,
  enclosed: 2600,
  cargo: 1800,
  equipment: 3000,
  flatbed: 2800,
  gooseneck: 4500,
  atv: 900,
};
